/**
 * Permission Groups Page
 * Phase 5: RBAC & Permission System - M4
 * 
 * Company owner permission group management page
 * Features: Grid card layout, search, CRUD operations
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import PermissionGroupFormModal from '../../components/company/PermissionGroupFormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PermissionButton from '../../components/common/PermissionButton';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import {
  fetchPermissionGroups,
  deletePermissionGroup
} from '../../redux/slices/permissionGroupSlice';
import permissionGroupService from '../../services/permissionGroupService';

const PermissionGroupsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { groups, isLoading } = useSelector((state) => state.permissionGroup);
  const { user: currentUser } = useSelector((state) => state.auth);
  
  // Permission-based guard (PAKET 3 - erken uygulandı)
  const hasPermission = useHasPermission('groups.view');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [allGroupsForCompanies, setAllGroupsForCompanies] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  
  // Check if user is system admin (for company filter)
  const isSystemAdmin = currentUser?.userType === 'system_admin';
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Extract unique companies from all groups (for system admin filter dropdown)
  const companies = useMemo(() => {
    if (!isSystemAdmin) return [];
    
    // Use cached all groups for company list (always extract from all groups)
    const sourceGroups = allGroupsForCompanies.length > 0 ? allGroupsForCompanies : groups;
    if (!sourceGroups || sourceGroups.length === 0) return [];
    
    const companyMap = new Map();
    sourceGroups.forEach(group => {
      if (group.companyId && group.companyId._id) {
        const companyId = group.companyId._id;
        const companyName = group.companyId.companyName || 'Bilinmeyen Şirket';
        if (!companyMap.has(companyId)) {
          companyMap.set(companyId, {
            _id: companyId,
            name: companyName
          });
        }
      }
    });
    
    return Array.from(companyMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name, 'tr')
    );
  }, [allGroupsForCompanies, groups, isSystemAdmin]);
  
  // Fetch all groups on initial load (for company list extraction - system admin only)
  useEffect(() => {
    if (hasPermission && isSystemAdmin && allGroupsForCompanies.length === 0) {
      // First load: Fetch all groups to extract company list
      dispatch(fetchPermissionGroups(null)).then((result) => {
        if (result.type === 'permissionGroup/fetchPermissionGroups/fulfilled') {
          setAllGroupsForCompanies(result.payload);
        }
      });
    }
  }, [dispatch, hasPermission, isSystemAdmin, allGroupsForCompanies.length]);
  
  // Fetch filtered groups when company filter changes
  useEffect(() => {
    if (!hasPermission) return;
    
    if (isSystemAdmin) {
      // System admin: Apply company filter (wait for initial all groups load if needed)
      if (allGroupsForCompanies.length > 0 || companyFilter === 'all') {
        // 'all' = null (tüm gruplar), 'global' = 'global' (sadece global), companyId = specific company
        const filterCompanyId = companyFilter === 'all' ? null : companyFilter;
        dispatch(fetchPermissionGroups(filterCompanyId));
      }
    } else {
      // Company owner: Always fetch their company's groups
      dispatch(fetchPermissionGroups(null));
    }
  }, [dispatch, hasPermission, companyFilter, isSystemAdmin, allGroupsForCompanies.length]);
  
  // Filter groups by search (using debounced search)
  const filteredGroups = groups.filter(g =>
    g.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    g.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );
  
  const handleEdit = (group) => {
    setEditingGroup(group);
    setModalOpen(true);
  };
  
  const handleDeleteClick = (group) => {
    setDeletingGroup(group);
    setDeleteDialogOpen(true);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleDeleteConfirm = async () => {
    await dispatch(deletePermissionGroup(deletingGroup._id));
    setDeleteDialogOpen(false);
    setDeletingGroup(null);
  };
  
  const handleModalClose = async () => {
    setModalOpen(false);
    setEditingGroup(null);
    
    // Refresh filtered groups (respects current companyFilter)
    const filterCompanyId = isSystemAdmin && companyFilter !== 'all' 
      ? (companyFilter === 'global' ? 'global' : companyFilter) 
      : null;
    await dispatch(fetchPermissionGroups(filterCompanyId));
    
    // Refresh company list if system admin (fetch all groups directly without updating Redux state)
    if (isSystemAdmin) {
      try {
        const allGroups = await permissionGroupService.getPermissionGroups(null);
        setAllGroupsForCompanies(allGroups);
      } catch (error) {
        console.error('Failed to fetch all groups for company list:', error);
      }
    }
  };
  
  // Permission-based guard (replaces userType guard)
  if (!hasPermission) {
    return <ForbiddenPage />;
  }
  
  return (
    <>
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mb: 3 }}
        >
          Geri Dön
        </Button>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Yetki Grupları
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSystemAdmin 
                ? 'Tüm yetki gruplarını görüntüleyin ve yönetin' 
                : 'Şirketiniz için özel yetki grupları oluşturun ve yönetin'}
            </Typography>
          </Box>
          <PermissionButton
            permission="groups.create"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Yeni Grup Oluştur
          </PermissionButton>
        </Box>
        
        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              placeholder="Grup adı veya açıklama ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            
            {/* Company Filter (System Admin Only) */}
            {isSystemAdmin && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Şirket Filtresi</InputLabel>
                <Select
                  value={companyFilter}
                  label="Şirket Filtresi"
                  onChange={(e) => setCompanyFilter(e.target.value)}
                >
                  <MenuItem value="all">Tümü (Global + Şirket Grupları)</MenuItem>
                  <MenuItem value="global">Sadece Global Gruplar</MenuItem>
                  {companies.map(company => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Paper>
        
        {isLoading && filteredGroups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Yetki grupları yükleniyor...
            </Typography>
          </Paper>
        ) : !isLoading && filteredGroups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {debouncedSearch || (isSystemAdmin && companyFilter !== 'all') 
                ? 'Arama sonucu bulunamadı' 
                : 'Henüz yetki grubu oluşturulmamış'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {!debouncedSearch && (!isSystemAdmin || companyFilter === 'all') 
                ? 'Şirketiniz için özel yetki grupları oluşturmak için "Yeni Grup Oluştur" butonunu kullanın'
                : 'Farklı bir arama terimi deneyin'}
            </Typography>
          </Paper>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Grup Adı</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell>Yetki Sayısı</TableCell>
                    <TableCell>Kullanıcı Sayısı</TableCell>
                    {isSystemAdmin && <TableCell>Şirket</TableCell>}
                    <TableCell align="right">İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGroups.map((group) => (
                    <TableRow key={group._id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SecurityIcon fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight="medium">
                            {group.name}
                          </Typography>
                          {group.isDefault && (
                            <Chip
                              label="Global"
                              size="small"
                              color="info"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {group.description || 'Açıklama yok'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={<SecurityIcon />}
                          label={`${group.permissionCount || 0} yetki`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={<GroupIcon />}
                          label={`${group.userCount || 0} kullanıcı`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>

                      {isSystemAdmin && (
                        <TableCell>
                          {group.companyId ? (
                            <Chip
                              icon={<BusinessIcon />}
                              label={group.companyId.companyName || 'Şirket Grubu'}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      )}

                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(group);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={group.isDefault ? 'Global gruplar silinemez' : 'Sil'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={group.isDefault}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!group.isDefault) {
                                    handleDeleteClick(group);
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredGroups.length > 0 && (
              <TablePagination
                component="div"
                count={filteredGroups.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Sayfa başına:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
            )}
          </Paper>
        )}
      </Container>
      
      {/* Modals */}
      <PermissionGroupFormModal
        open={modalOpen}
        onClose={handleModalClose}
        group={editingGroup}
      />
      
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Grup Sil"
        message={`"${deletingGroup?.name}" grubunu silmek istediğinize emin misiniz? Bu grup ${deletingGroup?.userCount || 0} kullanıcıdan kaldırılacaktır.`}
        confirmText="Sil"
        cancelText="İptal"
        severity="error"
      />
    </>
  );
};

export default PermissionGroupsPage;

